import { Component, OnInit } from "@angular/core";
import { CommonModule, formatDate } from "@angular/common";
import { RouterModule } from "@angular/router";

import { AppointmentsService } from "../../../shared/services/user-appointments.service";
import { BookingState, SalonMode } from "../../../shared/models/salon.model";
import { Appointment } from "../../../shared/models/appointment.model";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { environment } from "../../../../environments/environment";

@Component({
  selector: "customer-appointments",
  standalone: true,
  imports: [CommonModule, RouterModule, LoaderComponent],
  templateUrl: "./appointments.component.html",
  styleUrl: "./appointments.component.scss",
})
export class CustomerAppointmentsComponent implements OnInit {
  salonModes = SalonMode;
  bookingStates = BookingState;

  appointments: Appointment[] = [];
  isLoading = false;
  error: string | null = null;

  assetUrl = environment.assetBaseUrl;

  constructor(private appointmentsService: AppointmentsService) {}

  ngOnInit(): void {
    this.loadAppointments();
  }

  loadAppointments(): void {
    this.isLoading = true;
    this.error = null;

    this.appointmentsService.getMyAppointments().subscribe({
      next: (response) => {
        this.appointments = response.results || [];
        this.isLoading = false;
      },
      error: (err) => {
        console.error("Error loading appointments:", err);
        this.error = "Failed to load appointments. Please try again.";
        this.isLoading = false;
      },
    });
  }

  get totalSpend(): number {
    return this.appointments.reduce(
      (sum, appointment: any) => sum + Number(appointment.total_price || 0),
      0,
    );
  }

  get averageSpend(): number {
    if (!this.appointments.length) return 0;

    return this.totalSpend / this.appointments.length;
  }

  get confirmedAppointments(): number {
    return this.appointments.filter(
      (appointment) => appointment.status === "confirmed",
    ).length;
  }

  get completedAppointments(): number {
    return this.appointments.filter(
      (appointment) => appointment.status === "completed",
    ).length;
  }

  get pendingAppointments(): number {
    return this.appointments.filter(
      (appointment) => appointment.status === "pending",
    ).length;
  }

  get cancelledAppointments(): number {
    return this.appointments.filter(
      (appointment) => appointment.status === "cancelled",
    ).length;
  }

  getUpcomingAppointmentsCount(): number {
    const now = new Date();

    return this.appointments.filter((appointment) => {
      const start = new Date(appointment.start_time);

      return (
        ["confirmed", "pending"].includes(appointment.status) && start > now
      );
    }).length;
  }

  getBookingStateFromStatus(status: string): BookingState {
    switch (status) {
      case "confirmed":
        return BookingState.confirmed;
      case "completed":
        return BookingState.completed;
      case "cancelled":
        return BookingState.cancelled;
      case "pending":
        return BookingState.pending;
      default:
        return BookingState.pending;
    }
  }

  getTagText(status: string): string {
    switch (status) {
      case "confirmed":
        return "Confirmed";
      case "completed":
        return "Completed";
      case "cancelled":
        return "Booking Cancelled";
      case "pending":
        return "Pending";
      default:
        return "Pending";
    }
  }

  getTagState(status: string): string {
    switch (status) {
      case "confirmed":
        return "success";
      case "completed":
        return "default";
      case "cancelled":
        return "pending";
      case "pending":
        return "warning";
      default:
        return "warning";
    }
  }

  capitalizedStatus(status: string): string {
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : "";
  }

  statusClass(status: string): string {
    const normalized = String(status || "pending").toLowerCase();

    if (normalized === "completed") return "status-completed";
    if (normalized === "confirmed") return "status-confirmed";
    if (normalized === "cancelled") return "status-cancelled";

    return "status-pending";
  }

  formateDate(dateObj: string): string {
    if (!dateObj) return "";

    const date = new Date(dateObj);

    if (Number.isNaN(date.getTime())) return "";

    return formatDate(date, "EEEE, dd MMM", "en-US");
  }

  formateTime(dateObj: string): string {
    if (!dateObj) return "";

    const date = new Date(dateObj);

    if (Number.isNaN(date.getTime())) return "";

    return formatDate(date, "hh:mm a", "en-US");
  }

  formatDatePart(dateObj: string, dateFormat: string): string {
    if (!dateObj) return "";

    const date = new Date(dateObj);

    if (Number.isNaN(date.getTime())) return "";

    return formatDate(date, dateFormat, "en-US");
  }
}
